import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, IndianRupee, ShoppingBag } from "lucide-react";

const COLORS = ["#0F5132", "#059669", "#10B981", "#34D399", "#6EE7B7"];

export default function Reports() {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setData(data));
    api.get("/invoices", { params: { limit: 500 } }).then(({ data }) => setInvoices(data));
  }, []);

  if (!data) return <div className="text-slate-500">Loading reports…</div>;

  // Payment mix
  const mix = {};
  invoices.forEach((i) => { mix[i.payment_mode] = (mix[i.payment_mode] || 0) + i.grand_total; });
  const pie = Object.entries(mix).map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Reports &amp; Analytics</h1>
        <p className="text-sm text-slate-500">Sales, payment mix, and top movers.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi icon={IndianRupee} label="Total Revenue" value={`₹${Number(data.total_revenue).toLocaleString("en-IN")}`} />
        <Kpi icon={ShoppingBag} label="Total Invoices" value={data.total_invoices} />
        <Kpi icon={TrendingUp} label="Avg. Bill Value" value={data.total_invoices ? `₹${(data.total_revenue / data.total_invoices).toFixed(2)}` : "—"} />
      </div>

      <Card className="p-6 border-slate-200">
        <h3 className="font-display font-bold">Revenue — last 7 days</h3>
        <div className="h-72 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.last_7_days}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} />
              <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="revenue" stroke="#0F5132" strokeWidth={3} dot={{ r: 4, fill: "#059669" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-bold">Payment Mix</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" outerRadius={90} label>
                  {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-bold">Top selling medicines</h3>
          <div className="mt-3 space-y-2">
            {data.top_medicines.map((m, i) => (
              <div key={m.name} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-emerald-100 text-emerald-900 text-xs font-bold flex items-center justify-center">{i + 1}</div>
                  <div className="text-sm font-semibold">{m.name}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-num text-sm font-bold text-emerald-900">₹{m.revenue.toFixed(2)}</div>
                  <div className="text-xs text-slate-500">{m.qty} units</div>
                </div>
              </div>
            ))}
            {data.top_medicines.length === 0 && <p className="text-sm text-slate-400">No sales yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 border-slate-200">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-mono-num text-3xl font-extrabold text-slate-900 mt-1">{value}</div>
        </div>
        <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-700" />
        </div>
      </div>
    </Card>
  );
}
